// Timezone math for the Slot Availability Calendar widget.
//
// No date library is added for this feature (see the plan doc's open
// questions) — everything here is built on Intl.DateTimeFormat, which is
// enough for: converting a calendar-local wall-clock date/time into a real
// instant with the correct offset (what every write and range-query endpoint
// requires), formatting an instant back into that same local wall clock for
// display, and building the Monday-start month grid.
//
// All functions are pure and take `timeZone` explicitly rather than reading
// it from anywhere — SlotCalendarTimeHelper never assumes "Asia/Kolkata" the
// way BusinessHoursHelper does, since a listing's calendar can be any IANA
// zone the backend stores on SlotCalendarMeta.timezone.

/** yyyy-MM-dd, matching every date field the backend contract uses
 *  (anchorDate, fromDate, toDateExclusive, …). */
export type LocalDateString = string;

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * The IANA zone's UTC offset, in minutes, AT the given instant (so this is
 * DST-correct for zones that observe it, even though every zone this app
 * uses today is Asia/Kolkata and never changes).
 *
 * Algorithm: format `instant` as wall-clock fields IN `timeZone`, reinterpret
 * those same fields as if they were UTC, and diff against the real instant.
 * This is the standard technique for computing an IANA zone's offset without
 * a timezone database library — Intl.DateTimeFormat carries the tzdb, this
 * function just reads it via formatToParts instead of via a printed offset
 * string (which the "longOffset" timeZoneName option would give directly on
 * modern engines, but formatToParts numeric fields work everywhere Intl with
 * full ICU data is available, which is this app's baseline already —
 * BusinessHoursHelper and OrdersTab both rely on the same baseline).
 */
export function getTimeZoneOffsetMinutes(instant: Date, timeZone: string): number {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = dtf.formatToParts(instant).reduce<Record<string, string>>((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});
  // "24" from hourCycle:"h23" at local midnight prints as "24" on some
  // engines instead of "00" — normalize before feeding Date.UTC.
  const hour = parseInt(parts.hour, 10) % 24;
  const asIfUtc = Date.UTC(
    parseInt(parts.year, 10),
    parseInt(parts.month, 10) - 1,
    parseInt(parts.day, 10),
    hour,
    parseInt(parts.minute, 10),
    parseInt(parts.second, 10)
  );
  return Math.round((asIfUtc - instant.getTime()) / 60000);
}

/** "+05:30" / "-08:00" — the sign+HH:MM form every ISO instant in the
 *  backend contract uses. */
export function formatOffset(offsetMinutes: number): string {
  const sign = offsetMinutes < 0 ? "-" : "+";
  const abs = Math.abs(offsetMinutes);
  return `${sign}${pad2(Math.floor(abs / 60))}:${pad2(abs % 60)}`;
}

/**
 * Converts a calendar-local wall-clock date+time (as the calendar's own
 * timezone would read it) into the real instant it denotes.
 *
 * Two-pass: guess the instant by treating the wall time as if it were UTC,
 * read that guess's real offset in `timeZone`, and correct by it. A second
 * pass re-reads the offset at the corrected instant in case the first guess
 * landed on the wrong side of a DST transition — irrelevant for
 * Asia/Kolkata (no DST) but keeps this correct for any future timezone.
 * Skips/ambiguities at a DST boundary are the backend's problem to resolve
 * when materializing a schedule (see the proposal's "Skip nonexistent local
 * starts…" rule) — this function only has to get ordinary instants right for
 * building query ranges and previewing an owner's own input.
 */
export function zonedDateTimeToInstant(
  date: LocalDateString,
  time: string, // "HH:mm:ss" or "HH:mm"
  timeZone: string
): Date {
  const [y, mo, d] = date.split("-").map((s) => parseInt(s, 10));
  const timeParts = time.split(":").map((s) => parseInt(s, 10));
  const [h, mi, s = 0] = timeParts;

  const guessMs = Date.UTC(y, mo - 1, d, h, mi, s);
  const offset1 = getTimeZoneOffsetMinutes(new Date(guessMs), timeZone);
  const corrected1 = guessMs - offset1 * 60000;
  const offset2 = getTimeZoneOffsetMinutes(new Date(corrected1), timeZone);
  const correctedMs = offset2 === offset1 ? corrected1 : guessMs - offset2 * 60000;
  return new Date(correctedMs);
}

/** ISO instant string with the REAL offset at that instant in `timeZone` —
 *  what every `startsAt`/`endsAt`/`from`/`to` query param must be. */
export function formatIsoWithOffset(instant: Date, timeZone: string): string {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = dtf.formatToParts(instant).reduce<Record<string, string>>((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});
  const hour = pad2(parseInt(parts.hour, 10) % 24);
  const offset = formatOffset(getTimeZoneOffsetMinutes(instant, timeZone));
  return `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}:${parts.second}${offset}`;
}

/** The calendar-local date/instant range for one local calendar day, as real
 *  instants with the correct offset — [from, toExclusive). */
export function getLocalDayBounds(
  date: LocalDateString,
  timeZone: string
): { fromIso: string; toIsoExclusive: string } {
  const from = zonedDateTimeToInstant(date, "00:00:00", timeZone);
  const to = zonedDateTimeToInstant(addDays(date, 1), "00:00:00", timeZone);
  return {
    fromIso: formatIsoWithOffset(from, timeZone),
    toIsoExclusive: formatIsoWithOffset(to, timeZone),
  };
}

/** The calendar-local instant range for one local calendar month —
 *  `month` is 1-indexed (matches every other 1-indexed month value here). */
export function getLocalMonthBounds(
  year: number,
  month: number,
  timeZone: string
): { fromIso: string; toIsoExclusive: string; fromDate: LocalDateString; toDateExclusive: LocalDateString } {
  const fromDate = `${year}-${pad2(month)}-01`;
  const nextMonth = month === 12 ? { y: year + 1, m: 1 } : { y: year, m: month + 1 };
  const toDateExclusive = `${nextMonth.y}-${pad2(nextMonth.m)}-01`;
  const from = zonedDateTimeToInstant(fromDate, "00:00:00", timeZone);
  const to = zonedDateTimeToInstant(toDateExclusive, "00:00:00", timeZone);
  return {
    fromIso: formatIsoWithOffset(from, timeZone),
    toIsoExclusive: formatIsoWithOffset(to, timeZone),
    fromDate,
    toDateExclusive,
  };
}

export function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

/** month is 1-indexed. */
export function daysInMonth(year: number, month: number): number {
  const lengths = [31, isLeapYear(year) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return lengths[month - 1];
}

/** Pure calendar-date arithmetic — never touches Intl/timezone, since a plain
 *  yyyy-MM-dd string doesn't need one. Used for month-grid layout and for
 *  building the day-after/month-after boundary a query range needs. */
export function addDays(date: LocalDateString, delta: number): LocalDateString {
  const [y, m, d] = date.split("-").map((s) => parseInt(s, 10));
  // new Date(Date.UTC(...)) round-trips correctly across month/year
  // boundaries without any timezone involvement — this is UTC-anchored
  // purely as a calendar calculator, not a real instant.
  const utc = new Date(Date.UTC(y, m - 1, d + delta));
  return `${utc.getUTCFullYear()}-${pad2(utc.getUTCMonth() + 1)}-${pad2(utc.getUTCDate())}`;
}

/** Inclusive count of local calendar days from `startDate` to `endDate`
 *  (both yyyy-MM-dd, endDate >= startDate) - 1 for a single day, 3 for a
 *  Fri-through-Sun span. Pure calendar-date arithmetic (like addDays), since
 *  both inputs are already local dates and need no further timezone
 *  conversion - used to size a trip's band across the month grid (P3). */
export function countLocalDaysBetween(startDate: LocalDateString, endDate: LocalDateString): number {
  const [sy, sm, sd] = startDate.split("-").map((s) => parseInt(s, 10));
  const [ey, em, ed] = endDate.split("-").map((s) => parseInt(s, 10));
  const startUtc = Date.UTC(sy, sm - 1, sd);
  const endUtc = Date.UTC(ey, em - 1, ed);
  return Math.round((endUtc - startUtc) / 86_400_000) + 1;
}

/** 0 (Monday) – 6 (Sunday), ISO week order (unlike JS's native
 *  Date#getDay(), which is 0 = Sunday) — the grid brief specifies Monday-start
 *  weeks throughout. */
export function isoWeekdayIndex(date: LocalDateString): number {
  const [y, m, d] = date.split("-").map((s) => parseInt(s, 10));
  const jsDay = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 = Sunday
  return (jsDay + 6) % 7;
}

export interface MonthGridCell {
  date: LocalDateString;
  /** False for a leading/trailing cell from the adjacent month, shown
   *  disabled per the brief's "outside the current month" bar state. */
  inMonth: boolean;
}

/**
 * A Monday-start 6×7 grid covering the given month plus enough of the
 * adjacent months to fill whole weeks — exactly the layout
 * `display:grid; grid-template-columns:repeat(7,...)` in the brief expects.
 * Always 42 cells so every month's grid is the same height.
 */
export function buildMonthGrid(year: number, month: number): MonthGridCell[] {
  const firstOfMonth = `${year}-${pad2(month)}-01`;
  const leadingDays = isoWeekdayIndex(firstOfMonth); // days to back up to Monday
  const gridStart = addDays(firstOfMonth, -leadingDays);

  const cells: MonthGridCell[] = [];
  for (let i = 0; i < 42; i++) {
    const date = addDays(gridStart, i);
    const cellMonth = parseInt(date.slice(5, 7), 10);
    cells.push({ date, inMonth: cellMonth === month });
  }
  return cells;
}

/** Today's local calendar date AS SEEN in `timeZone` — "en-CA" is the
 *  well-known Intl locale trick for a bare yyyy-MM-dd (already used in
 *  OrdersTab.tsx for the same reason: it's the one common locale whose short
 *  date format IS yyyy-MM-dd, so no parsing of a formatted string is needed). */
export function getTodayLocalDate(timeZone: string, now: Date = new Date()): LocalDateString {
  return new Intl.DateTimeFormat("en-CA", { timeZone }).format(now);
}

/** The local calendar date (same "en-CA" yyyy-MM-dd trick as
 *  getTodayLocalDate, generalized to any instant) that `iso` falls on AS SEEN
 *  in `timeZone` - how the P2 month-grid buckets availability items onto
 *  day cells. */
export function getLocalDateOfInstant(iso: string, timeZone: string): LocalDateString {
  return new Intl.DateTimeFormat("en-CA", { timeZone }).format(new Date(iso));
}

/** "6:00 AM" — a slot's start/end time as seen in the calendar's own
 *  timezone, for chip/detail display. */
export function formatSlotTime(iso: string, timeZone: string): string {
  const date = parseDisplayDate(iso);
  if (!date) return "";
  return new Intl.DateTimeFormat("en-IN", {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
}

/** Display formatters must never throw on a bad/missing timestamp - an
 *  Intl RangeError here unmounts the whole screen. Returns null instead;
 *  callers render "" (and dev builds log the offending value once). */
function parseDisplayDate(iso: string): Date | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    if (import.meta.env.DEV) console.warn("[SlotCalendar] unparseable timestamp", JSON.stringify(iso));
    return null;
  }
  return date;
}

/** "Thu, 24 Sep" — a date's short display title. `iso` may be an instant or
 *  a bare yyyy-MM-dd; both parse fine via `new Date(...)`. */
export function formatDayTitle(iso: string, timeZone: string): string {
  const date = parseDisplayDate(iso);
  if (!date) return "";
  return new Intl.DateTimeFormat("en-IN", {
    timeZone,
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(date);
}

/** "November 2026" — month-nav title / year-tile heading. */
export function formatMonthTitle(year: number, month: number, timeZone: string): string {
  // Built from local noon of the 1st, not local midnight, so a huge negative
  // UTC offset can never roll the displayed month back by a day.
  const instant = zonedDateTimeToInstant(`${year}-${pad2(month)}-01`, "12:00:00", timeZone);
  return new Intl.DateTimeFormat("en-IN", { timeZone, month: "long", year: "numeric" }).format(instant);
}

/** "India Standard Time" — the footer's "Times in {zone name}" label. */
export function getTimeZoneDisplayName(timeZone: string, now: Date = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    timeZoneName: "long",
  }).formatToParts(now);
  return parts.find((p) => p.type === "timeZoneName")?.value ?? timeZone;
}
